import React, { useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Typography,
  InputAdornment,
  OutlinedInput,
  InputLabel,
  FormControl,
  IconButton,
  Box,
  Button,
} from '@mui/material';
import {
  getApiErrorMessage,
  useApiGet,
  useApiSend,
} from '../../utils/httpClientTs';
import {
  getConversationMessages,
  createMessage,
} from '../../endpoints/messagesTs';
import {
  ConversationMessagesResponse,
  MessageDialogProps,
  ConversationMessageCreateRequest,
} from '../../types/messages';
import { Send, ArrowBack } from '@mui/icons-material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const MessageDialog: React.FC<MessageDialogProps> = ({
  userId,
  senderId,
  content,
  timestamp,
  senderName,
}) => {
  const isOwnMessage = userId === senderId;

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        backgroundColor: isOwnMessage ? 'primary.main' : 'grey.100',
        color: isOwnMessage ? 'primary.contrastText' : 'text.primary',
        wordWrap: 'break-word',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 'fit-content',
      }}
    >
      <Typography variant="body2" sx={{ mb: 1 }}>
        {content}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: isOwnMessage ? 'primary.contrastText' : 'text.secondary',
          opacity: 0.8,
          fontSize: '0.75rem',
        }}
      >
        {dayjs.utc(timestamp).local().format('M/DD/YYYY h:mm A')} - {senderName}
      </Typography>
    </Box>
  );
};

export default function ConversationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const userId = localStorage.getItem('user_id');
  const [isSaving, setIsSaving] = useState(false);
  const [errorSaving, setErrorSaving] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState('');

  const {
    data: conversationMessage,
    isFetching,
    refetch,
    isError,
    error,
  } = useApiGet<ConversationMessagesResponse>(
    ['conversations', id],
    getConversationMessages,
    {
      enabled: userId !== null && id !== undefined,
      retry: 1,
      refetchOnWindowFocus: true, // Refetch when window regains focus
    },
    { id: id! }
  );

  const { mutate: sendMessage } = useApiSend<
    { success: boolean },
    ConversationMessageCreateRequest
  >(
    createMessage,
    (data) => {
      setIsSaving(false);
      setNewMessage('');
      setErrorSaving(null);
      refetch();
    },
    (error) => {
      setErrorSaving(getApiErrorMessage(error));
      setIsSaving(false);
    }
  );

  const handleSendMessage = useCallback(() => {
    if (newMessage.trim() === '' || !id) {
      return;
    }
    setIsSaving(true);
    setErrorSaving(null);

    sendMessage({
      conversationId: id,
      message: newMessage.trim(),
    });
  }, [newMessage, id, sendMessage]);

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage]
  );

  const handleBackToList = useCallback(() => {
    navigate('/conversations');
  }, [navigate]);

  if (isFetching && !conversationMessage) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  /* Full-page error only when we have no cached thread (e.g. initial load failed). */
  if (!isFetching && isError && !conversationMessage) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" sx={{ mb: 2 }}>
          {getApiErrorMessage(error)}
        </Typography>
        <Button
          variant="outlined"
          sx={{ mr: 1 }}
          onClick={() => void refetch()}
        >
          Retry
        </Button>
        <Button variant="outlined" onClick={handleBackToList}>
          Back to Messages
        </Button>
      </Box>
    );
  }

  const messages = conversationMessage?.messages ?? [];

  if (!conversationMessage || messages.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        {isError && conversationMessage && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => void refetch()}
              >
                Retry
              </Button>
            }
          >
            Couldn&apos;t refresh: {getApiErrorMessage(error)}
          </Alert>
        )}
        <Typography>No messages found.</Typography>
        <Button
          sx={{ mt: 2 }}
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={handleBackToList}
        >
          Back to Messages
        </Button>
      </Box>
    );
  }

  // Sort messages by timestamp (newest first)
  const orderedMessages = [...messages].sort((a, b) => {
    return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
  });

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {isError && conversationMessage && (
        <Alert
          severity="warning"
          sx={{ mx: 2, mt: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => void refetch()}>
              Retry
            </Button>
          }
        >
          Couldn&apos;t refresh: {getApiErrorMessage(error)}
        </Alert>
      )}
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <IconButton onClick={handleBackToList} color="primary">
          <ArrowBack />
        </IconButton>
        <Typography variant="h5" component="h1" sx={{ flexGrow: 1 }}>
          {conversationMessage.title}
        </Typography>
      </Box>

      {/* Messages Area */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {orderedMessages.map((message) => (
          <Box
            key={message.id}
            sx={{
              width: { xs: '100%', sm: '50%' },
              alignSelf:
                userId === message.sender_id ? 'flex-end' : 'flex-start',
            }}
          >
            <MessageDialog
              userId={userId}
              senderId={message.sender_id}
              content={message.content}
              timestamp={message.timestamp}
              senderName={message.sender_name}
            />
          </Box>
        ))}
      </Box>

      {/* Message Input */}
      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        <FormControl
          fullWidth
          variant="outlined"
          size="small"
          error={!!errorSaving}
        >
          <InputLabel htmlFor="new-message">New Message</InputLabel>
          <OutlinedInput
            id="new-message"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  onClick={handleSendMessage}
                  disabled={isSaving || newMessage.trim() === ''}
                  color="primary"
                >
                  <Send />
                </IconButton>
              </InputAdornment>
            }
            label="New Message"
            multiline
            maxRows={4}
            onKeyDown={handleKeyPress}
            placeholder="Type your message..."
          />
          {errorSaving && (
            <Typography
              variant="caption"
              color="error"
              sx={{ mt: 0.5, ml: 1.75 }}
            >
              {errorSaving}
            </Typography>
          )}
        </FormControl>
      </Box>
    </Box>
  );
}
